/**
 * AssetPlayer — PRD v.1-D §A.15 / FR-008.
 *
 * Headless Vidstack wrapper. Selects the audio or video chrome by
 * `descriptor.mediaKind`; exposes the domain-shaped `AssetPlayerDescriptor`
 * surface so call sites never touch Vidstack internals (the FR-008
 * "domain-shaped descriptor, not library internals" invariant).
 *
 * Implementation constraints from A.15 — all load-bearing:
 *
 *   1) Headless build only. We import `MediaPlayer` + `MediaProvider`
 *      (+ `Track`) and the COMPOSABLE control primitives (`Controls`,
 *      `PlayButton`, `TimeSlider`, … — all part of the `@vidstack/react`
 *      main entry) from `@vidstack/react`. We DO NOT import the default
 *      layout (`@vidstack/react/player/layouts/default`) — that layout
 *      alone exceeds the 75 KB compressed cap (NFR-009 / size-limit). The
 *      control icons are local inline SVGs (`./icons`) — not the
 *      `@vidstack/react/icons` subpath, whose first import would force a
 *      vite dep re-optimization mid-session; inline SVG is CSP-safe and
 *      keeps the package self-contained.
 *
 *   2) `key` bound to `descriptor.source.url` forces a fresh Vidstack
 *      instance on every source swap. Carry-over state (residual buffer,
 *      stale current-time, captions still mounted from the previous
 *      source) is a recurring class of bug in media players; the remount
 *      kills it at the React reconciler level.
 *
 *   3) `crossOrigin="anonymous"` is mandatory so the QoeBridge can read
 *      cross-origin media metrics (buffered ranges, currentTime, current
 *      level bitrate). The CDN must mirror this with an
 *      `Access-Control-Allow-Origin` header matching the console origin —
 *      verified at deploy (NFR-008 / T338).
 *
 *   4) `playsInline` keeps iOS Safari from auto-opening the native
 *      fullscreen player on the first `play()`. Inline playback is the
 *      console's default surface for marker editing.
 *
 *   5) If a source is HLS, Vidstack is given a SELF-HOSTED `hls.js`
 *      bundle. Vidstack's default behaviour is to inject a `<script>`
 *      from a third-party content-delivery network, which the strict
 *      CSP (`script-src 'self'`; PRD A.10) would block. Progressive
 *      MP3/MP4 enclosures need no `hls.js`. This file MUST NOT reference
 *      the public script-CDN hosts — the source-level pin in the §A.15
 *      test asserts the absence of those hostnames here.
 *
 *   6) Accessibility — the layout root carries `role="region"` with a
 *      non-empty `aria-label` so screen readers announce the surface
 *      (NFR-010). The label is the descriptor `title`, falling back to
 *      a kind-specific default. Every control carries a Spanish
 *      `aria-label` to match the rest of the console.
 *
 *   7) The QoE bridge is mounted as a sibling to the player. It reads
 *      from the underlying HTML media element via a ref and POSTS
 *      DIRECTLY to `/qoe/beacon/{channelId}` — it does not route through
 *      the oo-edge snippet generator (A.15 / FR-009).
 *
 *   8) Playback controls. The headless build ships NO control chrome, so
 *      we compose the full transport ourselves from Vidstack's headless
 *      primitives: a center play/replay overlay, a buffering spinner,
 *      click-to-toggle + double-click-fullscreen gestures, and a bottom
 *      control bar (play/pause, seek ±10 s, a scrubber with a hover-time
 *      preview, current/duration time OR a live-edge button for live
 *      streams, volume + mute, captions when tracks exist, picture-in-
 *      picture, and fullscreen). Live vs. on-demand chrome is driven off
 *      the real `useMediaState('live')` value, so an HLS manifest with no
 *      `#EXT-X-ENDLIST` (a live channel) shows the live affordances and a
 *      VOD asset shows the scrubber — no descriptor flag needed.
 *
 *      Video autostarts MUTED (`autoPlay` + `muted`): browsers block
 *      autoplay of UNMUTED media without a user gesture, so an unmuted
 *      autostart would silently sit paused. Muted autostart is the
 *      standard live-monitor behaviour and is always permitted; the
 *      operator unmutes with one click. Audio never autostarts and is
 *      never force-muted (muting audio you can't hear is the wrong
 *      default).
 */
import { useCallback, useEffect, useImperativeHandle, useRef, type JSX, type Ref } from 'react';
import {
  MediaPlayer,
  MediaProvider,
  Track,
  Gesture,
  Spinner,
  PlayButton,
  MuteButton,
  CaptionButton,
  PIPButton,
  FullscreenButton,
  SeekButton,
  LiveButton,
  TimeSlider,
  VolumeSlider,
  Time,
  useMediaState,
  isHLSProvider,
  type MediaPlayerInstance,
  type MediaProviderAdapter,
  type TextTrack as VidstackTextTrack,
} from '@vidstack/react';
import {
  PlayIcon,
  PauseIcon,
  ReplayIcon,
  MuteIcon,
  VolumeLowIcon,
  VolumeHighIcon,
  FullscreenIcon,
  FullscreenExitIcon,
  PictureInPictureIcon,
  PictureInPictureExitIcon,
  ClosedCaptionsIcon,
  ClosedCaptionsOnIcon,
  SeekBackwardIcon,
  SeekForwardIcon,
} from './icons';
import HLS from 'hls.js';
import './asset-player.css';
import type { AssetPlayerDescriptor } from './types';
import { QoeBridge } from './QoeBridge';
import { logger } from '../../lib/logger';

/**
 * The seek handle the console reaches the player through (card MA-34). A
 * seek for an ANALYSIS time (a deep link, a transcript cue, a search hit)
 * goes through `seekMs`, which applies the origin correction below; the
 * transport controls seek through Vidstack directly and are never shifted.
 */
export interface AssetPlayerHandle {
  /** Seek to an analysis time: `currentTime = ms / 1000` plus the origin
   *  correction (CONTRACTS.md time rules 5 and 9). */
  seekMs(ms: number): void;
}

export interface AssetPlayerProps {
  descriptor: AssetPlayerDescriptor;
  /** Receives the seek handle. */
  playerRef?: Ref<AssetPlayerHandle>;
  /** Fires once the media can play — the moment a deep link may seek. */
  onLoaded?: () => void;
}

/** Ticks per second of the analysis time axis (CONTRACTS.md section 5). */
const TICKS_PER_SECOND = 90_000;
/** The player's origin and the run's may differ by this much (1 ms) before
 *  a correction is applied (time rule 9). */
const ORIGIN_TOLERANCE_TICKS = 90;

/**
 * The origin correction of time rule 9, in ticks: `originTicks − initTicks`
 * when the two differ by more than the tolerance, otherwise 0. `initTicks`
 * is hls.js's `initPTS` converted to ticks with the event's `timescale`
 * (`initPTS` itself for a transport stream, whose timescale is 90,000) —
 * the console's one conversion of a player time.
 */
function originOffsetTicks(originTicks: number | undefined, initPTS: number, timescale: number): number {
  if (originTicks === undefined || !Number.isFinite(initPTS) || !(timescale > 0)) return 0;
  const initTicks = Math.round((initPTS * TICKS_PER_SECOND) / timescale);
  const diff = originTicks - initTicks;
  return Math.abs(diff) > ORIGIN_TOLERANCE_TICKS ? diff : 0;
}

/**
 * Stable accessible label for the layout root. Falls back to a kind-specific
 * literal when the descriptor omits a title (NFR-010 still requires a
 * non-empty `aria-label`).
 */
function layoutLabel(descriptor: AssetPlayerDescriptor): string {
  if (descriptor.title && descriptor.title.length > 0) {
    return descriptor.title;
  }
  return descriptor.mediaKind === 'audio' ? 'Audio player' : 'Video player';
}

/**
 * Render the WebVTT/captions tracks supplied on the descriptor. Vidstack's
 * `<Track>` re-emits each as a real `<track>` so native browser caption
 * rendering and the bridge's `useActiveTextTrack` hook both see them.
 */
function renderTracks(descriptor: AssetPlayerDescriptor): JSX.Element[] {
  return (descriptor.textTracks ?? []).map((t, i) => (
    <Track
      key={`${t.src}#${i}`}
      src={t.src}
      kind={t.kind}
      label={t.label}
      lang={t.language}
      default={t.default}
    />
  ));
}

/* ---------------------------------------------------------------------------
 * Headless control primitives. Each is a tiny child of `<MediaPlayer>` so it
 * can read live media state via `useMediaState(...)` and swap its icon. The
 * underlying Vidstack buttons own the toggle behaviour, ARIA pressed-state,
 * and keyboard handling; we only choose the glyph.
 * ------------------------------------------------------------------------- */

function PlayToggle(): JSX.Element {
  const paused = useMediaState('paused');
  const ended = useMediaState('ended');
  return (
    <PlayButton className="ap-btn" aria-label={paused ? 'Reproducir' : 'Pausar'}>
      {ended ? <ReplayIcon /> : paused ? <PlayIcon /> : <PauseIcon />}
    </PlayButton>
  );
}

function MuteToggle(): JSX.Element {
  const muted = useMediaState('muted');
  const volume = useMediaState('volume');
  const silent = muted || volume === 0;
  return (
    <MuteButton className="ap-btn" aria-label={silent ? 'Activar sonido' : 'Silenciar'}>
      {silent ? <MuteIcon /> : volume < 0.5 ? <VolumeLowIcon /> : <VolumeHighIcon />}
    </MuteButton>
  );
}

function CaptionToggle(): JSX.Element {
  const track = useMediaState('textTrack');
  const on = !!track;
  return (
    <CaptionButton className="ap-btn" aria-label={on ? 'Ocultar subtítulos' : 'Mostrar subtítulos'}>
      {on ? <ClosedCaptionsOnIcon /> : <ClosedCaptionsIcon />}
    </CaptionButton>
  );
}

function PipToggle(): JSX.Element {
  const pip = useMediaState('pictureInPicture');
  return (
    <PIPButton className="ap-btn" aria-label={pip ? 'Salir de imagen en imagen' : 'Imagen en imagen'}>
      {pip ? <PictureInPictureExitIcon /> : <PictureInPictureIcon />}
    </PIPButton>
  );
}

function FullscreenToggle(): JSX.Element {
  const fullscreen = useMediaState('fullscreen');
  return (
    <FullscreenButton
      className="ap-btn"
      aria-label={fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
    >
      {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
    </FullscreenButton>
  );
}

/** The horizontal scrubber with a hover-time preview (on-demand only). */
function Scrubber(): JSX.Element {
  return (
    <TimeSlider.Root className="ap-time-slider">
      <TimeSlider.Track className="ap-slider-track">
        <TimeSlider.TrackFill className="ap-slider-track ap-slider-fill" />
        <TimeSlider.Progress className="ap-slider-track ap-slider-progress" />
      </TimeSlider.Track>
      <TimeSlider.Thumb className="ap-slider-thumb" />
      <TimeSlider.Preview className="ap-slider-preview">
        <TimeSlider.Value className="ap-slider-value" />
      </TimeSlider.Preview>
    </TimeSlider.Root>
  );
}

/** The bottom control bar shared between layouts. `video` toggles the
 *  video-only affordances (seek, captions, PiP, fullscreen). */
function ControlBar({ video, hasTracks }: { video: boolean; hasTracks: boolean }): JSX.Element {
  const live = useMediaState('live');
  return (
    <>
      {!live && <Scrubber />}
      <div className="ap-control-row">
        <PlayToggle />
        {video && !live && (
          <>
            <SeekButton className="ap-btn" seconds={-10} aria-label="Retroceder 10 segundos">
              <SeekBackwardIcon />
            </SeekButton>
            <SeekButton className="ap-btn" seconds={10} aria-label="Avanzar 10 segundos">
              <SeekForwardIcon />
            </SeekButton>
          </>
        )}
        <div className="ap-volume">
          <MuteToggle />
          <VolumeSlider.Root className="ap-volume-slider">
            <VolumeSlider.Track className="ap-slider-track">
              <VolumeSlider.TrackFill className="ap-slider-track ap-slider-fill" />
            </VolumeSlider.Track>
            <VolumeSlider.Thumb className="ap-slider-thumb" />
          </VolumeSlider.Root>
        </div>

        {live ? (
          <LiveButton className="ap-live" aria-label="Ir al directo">
            <span className="ap-live-dot" />
            EN VIVO
          </LiveButton>
        ) : (
          <div className="ap-time">
            <Time className="ap-time-current" type="current" />
            <span className="ap-time-sep">/</span>
            <Time className="ap-time-duration" type="duration" />
          </div>
        )}

        <div className="ap-spacer" />

        {hasTracks && <CaptionToggle />}
        {video && <PipToggle />}
        {video && <FullscreenToggle />}
      </div>
    </>
  );
}

/** Center play/replay overlay shown while paused, plus the buffering spinner. */
function CenterOverlay(): JSX.Element {
  const paused = useMediaState('paused');
  const ended = useMediaState('ended');
  const waiting = useMediaState('waiting');
  const canPlay = useMediaState('canPlay');
  return (
    <>
      {(paused || ended) && (
        <div className="ap-center-overlay">
          <PlayButton className="ap-center-btn" aria-label={ended ? 'Reproducir de nuevo' : 'Reproducir'}>
            {ended ? <ReplayIcon /> : <PlayIcon />}
          </PlayButton>
        </div>
      )}
      {waiting && canPlay && (
        <div className="ap-buffering" role="status" aria-label="Cargando">
          <Spinner.Root className="ap-spinner" size={64}>
            <Spinner.Track className="ap-spinner-track" width={6} />
            <Spinner.TrackFill className="ap-spinner-fill" width={6} fillPercent={50} />
          </Spinner.Root>
        </div>
      )}
    </>
  );
}

export function AssetPlayer({ descriptor, playerRef, onLoaded }: AssetPlayerProps): JSX.Element {
  // Ref onto the underlying <video>/<audio> element Vidstack mounts. The
  // QoeBridge subscribes to its events to compute the three real QoE
  // quantities. Under jsdom + the Vidstack mock the element is never
  // mounted and the ref stays null; the bridge then no-ops, which is the
  // correct test-time behaviour (the bridge is exercised directly with a
  // fake media element in the FR-009 tests).
  const mediaRef = useRef<HTMLMediaElement | null>(null);

  /* -------------------------------------------------------------------------
   * The player's origin correction (CONTRACTS.md time rule 9, D-03; card
   * MA-34). hls.js reports its origin for the playing rendition through
   * `INIT_PTS_FOUND`; the run's origin P0 arrives as `descriptor.originTicks`.
   * When they differ by more than 90 ticks, every analysis seek and every
   * side-loaded cue is shifted by `(originTicks − initTicks) / 90000` s.
   * Without `originTicks`, or without the event (Safari's native player),
   * nothing is applied. The log carries keys only: identifiers and ticks.
   * ----------------------------------------------------------------------- */
  const player = useRef<MediaPlayerInstance | null>(null);
  const offsetTicks = useRef(0);
  // Per side-loaded track, the offset (in ticks) its cues already carry, so a
  // repeated report or a re-run of the shift never moves a cue twice.
  const shifted = useRef(new WeakMap<VidstackTextTrack, number>());
  const originTicksRef = useRef(descriptor.originTicks);
  originTicksRef.current = descriptor.originTicks;

  const shiftLoadedCues = useCallback((): void => {
    const p = player.current;
    if (!p) return;
    const target = offsetTicks.current;
    for (const track of p.textTracks) {
      if (track.readyState !== 2) continue;
      const applied = shifted.current.get(track) ?? 0;
      if (applied === target) continue;
      const deltaSeconds = (target - applied) / TICKS_PER_SECOND;
      for (const cue of track.cues) {
        cue.startTime += deltaSeconds;
        cue.endTime += deltaSeconds;
      }
      shifted.current.set(track, target);
    }
  }, []);

  const onInitPtsFound = useCallback(
    (_event: string, data: { id?: string; initPTS: number; timescale: number }): void => {
      // Only the main playlist's report is the playing rendition's origin; an
      // alternate audio track reports its own.
      if (data.id !== undefined && data.id !== 'main') return;
      const origin = originTicksRef.current;
      const next = originOffsetTicks(origin, data.initPTS, data.timescale);
      if (next !== offsetTicks.current) {
        offsetTicks.current = next;
        if (next !== 0 && origin !== undefined) {
          logger.info('player_origin_offset', {
            assetId:     descriptor.assetId,
            originTicks: origin,
            initTicks:   origin - next,
            offsetTicks: next,
          });
        }
      }
      shiftLoadedCues();
    },
    [descriptor.assetId, shiftLoadedCues],
  );

  /**
   * Self-host `hls.js` for HLS sources. Ingested assets are transcoded to HLS,
   * so the player's real source is a `.m3u8` manifest — Vidstack's default is
   * to fetch `hls.js` from a third-party CDN; we hand it the bundled copy
   * instead (no CDN dependency, CSP-safe). MP4/MP3 progressive sources never
   * reach the HLS provider, so this is a no-op for them. Through the provider's
   * instance the player also listens to `INIT_PTS_FOUND` (time rule 9).
   */
  const onProviderChange = useCallback(
    (provider: MediaProviderAdapter | null): void => {
      if (isHLSProvider(provider)) {
        provider.library = HLS;
        provider.onInstance((hls) => {
          hls.on(HLS.Events.INIT_PTS_FOUND, onInitPtsFound);
        });
      }
    },
    [onInitPtsFound],
  );

  // A track that loads after the report gets the same shift.
  useEffect(() => {
    const p = player.current;
    if (!p) return;
    const list = p.textTracks;
    const disposers: Array<() => void> = [];
    const watch = (track: VidstackTextTrack): void => {
      track.addEventListener('load', shiftLoadedCues);
      disposers.push(() => track.removeEventListener('load', shiftLoadedCues));
    };
    for (const track of list) watch(track);
    const onAdd = (event: { detail: VidstackTextTrack }): void => { watch(event.detail); shiftLoadedCues(); };
    list.addEventListener('add', onAdd as unknown as EventListener);
    disposers.push(() => list.removeEventListener('add', onAdd as unknown as EventListener));
    shiftLoadedCues();
    return () => { for (const d of disposers) d(); };
  }, [descriptor.source.url, shiftLoadedCues]);

  useImperativeHandle(
    playerRef,
    () => ({
      seekMs(ms: number): void {
        const p = player.current;
        if (!p) return;
        p.currentTime = ms / 1000 + offsetTicks.current / TICKS_PER_SECOND;
      },
    }),
    [],
  );

  const hasTracks = (descriptor.textTracks ?? []).length > 0;

  // Common Vidstack props shared between the audio and video layouts. The
  // `key` is bound to the source URL so a source swap forces a fresh
  // instance (A.15 implementation constraint #2 — pinned by the
  // §A.15 source-level regex `/key\s*=\s*\{[^}]*\.source\.url[^}]*\}/`).
  const playerProps = {
    src:         descriptor.source.url,
    crossOrigin: 'anonymous' as const,
    playsInline: true,
    title:       descriptor.title,
    poster:      descriptor.posterUrl,
  };

  if (descriptor.mediaKind === 'audio') {
    return (
      <div
        data-testid="asset-player-audio-layout"
        role="region"
        aria-label={layoutLabel(descriptor)}
        className="ap-root ap-root--audio"
      >
        <MediaPlayer
          key={descriptor.source.url}
          ref={player}
          onProviderChange={onProviderChange}
          onCanPlay={onLoaded}
          crossOrigin={playerProps.crossOrigin}
          playsInline={playerProps.playsInline}
          src={playerProps.src}
          title={playerProps.title}
        >
          <MediaProvider />
          {renderTracks(descriptor)}
          <div className="ap-audio-controls">
            <ControlBar video={false} hasTracks={hasTracks} />
          </div>
        </MediaPlayer>
        {mediaRef.current ? (
          <QoeBridge channelId={descriptor.channelId} media={mediaRef.current} />
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-testid="asset-player-video-layout"
      role="region"
      aria-label={layoutLabel(descriptor)}
      className="ap-root ap-root--video"
    >
      <MediaPlayer
        key={descriptor.source.url}
        ref={player}
        onProviderChange={onProviderChange}
        onCanPlay={onLoaded}
        crossOrigin={playerProps.crossOrigin}
        playsInline={playerProps.playsInline}
        autoPlay
        muted
        src={playerProps.src}
        title={playerProps.title}
        poster={playerProps.poster}
      >
        {/* Video surface: the media frame only. Controls live BELOW it (see
         *  `.ap-video-stage` / `.ap-video-controls`) so they never obscure the
         *  picture. The stage keeps the absolutely-positioned gesture/overlay/
         *  spinner layers confined to the frame; the control bar is a normal
         *  flow sibling rendered after it. */}
        <div className="ap-video-stage">
          <MediaProvider />
          {renderTracks(descriptor)}

          {/* Click anywhere to toggle play; double-click to toggle fullscreen. */}
          <Gesture className="ap-gesture" event="pointerup" action="toggle:paused" />
          <Gesture className="ap-gesture" event="dblpointerup" action="toggle:fullscreen" />

          <CenterOverlay />
        </div>

        {/* Dedicated, always-visible control bar beneath the video frame. It is
         *  still a descendant of `<MediaPlayer>`, so every control reads the
         *  same live media state via `useMediaState(...)` and drives the same
         *  remote — these are the real transport controls, not a decorative
         *  copy. */}
        <div className="ap-video-controls">
          <ControlBar video hasTracks={hasTracks} />
        </div>
      </MediaPlayer>
      {mediaRef.current ? (
        <QoeBridge channelId={descriptor.channelId} media={mediaRef.current} />
      ) : null}
    </div>
  );
}
